import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';

interface FAQ {
  question: string;
  answer: string;
}

interface Feature {
  icon: string;
  title: string;
  description: string;
}

interface Testimonial {
  quote: string;
  author: string;
  position: string;
  company: string;
}

interface InsightCategory {
  name: string;
  description: string;
  icon: string;
}

@Component({
  selector: 'app-customer-insight',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <!-- Hero Section -->
    <section class="relative bg-gradient-to-br from-gray-900 to-blue-900 text-white py-16 md:py-24">
      <div class="absolute inset-0 bg-pattern opacity-20"></div>
      <div class="container mx-auto px-4 relative z-10">
        <div class="flex flex-col md:flex-row items-center">
          <div class="md:w-1/2 mb-10 md:mb-0">
            <div class="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm text-sm font-medium mb-6">
              <span class="w-2 h-2 rounded-full bg-orange-400 animate-pulse"></span>
              <span>Customer Intelligence</span>
            </div>
            <h1 class="text-3xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Customer Insight
            </h1>
            <p class="text-xl md:text-2xl mb-8 text-blue-100">
              Unlock deeper understanding of your customers with AI-powered analytics, behavioral insights, and predictive modeling to enhance customer experiences, increase loyalty, and drive revenue growth.
            </p>
            <div class="flex flex-col sm:flex-row gap-4">
              <a href="#contact" class="bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 px-6 rounded-lg transition duration-300 text-center">
                Request a Demo
              </a>
              <a href="#features" class="bg-transparent hover:bg-white/10 border border-white text-white font-bold py-3 px-6 rounded-lg transition duration-300 text-center">
                Explore Features
              </a>
            </div>
          </div>
          <div class="md:w-1/2">
            <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-2xl">
              <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1000" 
                   alt="Customer Insight Dashboard showing user behavior analytics" 
                   class="rounded-lg w-full">
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Key Features Section -->
    <section class="py-16 bg-gray-50" id="features">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Key Features</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Our Customer Insight platform provides comprehensive tools to understand customer behavior, preferences, and needs through advanced analytics, segmentation, and journey mapping to deliver personalized experiences at scale.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          @for (feature of features; track feature.title) {
            <div class="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition duration-300 border border-gray-100">
              <div class="text-orange-600 text-4xl mb-4">
                <i class="fas {{ feature.icon }}"></i>
              </div>
              <h3 class="text-xl font-bold mb-3 text-gray-900">{{ feature.title }}</h3>
              <p class="text-gray-600">{{ feature.description }}</p>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- Insight Categories Section -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Comprehensive Customer Insights</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Gain a 360° view of your customers across multiple dimensions.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
          @for (category of insightCategories; track category.name) {
            <div class="bg-white rounded-xl p-8 shadow-lg border border-gray-100 flex">
              <div class="mr-6">
                <div class="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 text-2xl">
                  <i class="fas {{ category.icon }}"></i>
                </div>
              </div>
              <div>
                <h3 class="text-xl font-bold mb-2 text-gray-900">{{ category.name }}</h3>
                <p class="text-gray-600">{{ category.description }}</p>
              </div>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- Behavioral Analysis Section -->
    <section class="py-16 bg-gray-50">
      <div class="container mx-auto px-4">
        <div class="flex flex-col md:flex-row items-center">
          <div class="md:w-1/2 mb-10 md:mb-0 md:pr-12">
            <h2 class="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Advanced Behavioral Analysis</h2>
            <p class="text-xl text-gray-600 mb-6">
              Our AI-powered platform analyzes customer behavior patterns to provide actionable insights for your business.
            </p>
            <ul class="space-y-4">
              <li class="flex items-start">
                <div class="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center mr-3 mt-1">
                  <i class="fas fa-check text-orange-600 text-sm"></i>
                </div>
                <p class="text-gray-700">Identify customer journey patterns and pain points</p>
              </li>
              <li class="flex items-start">
                <div class="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center mr-3 mt-1">
                  <i class="fas fa-check text-orange-600 text-sm"></i>
                </div>
                <p class="text-gray-700">Predict future purchasing behavior with machine learning</p>
              </li>
              <li class="flex items-start">
                <div class="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center mr-3 mt-1">
                  <i class="fas fa-check text-orange-600 text-sm"></i>
                </div>
                <p class="text-gray-700">Segment customers based on behavior and preferences</p>
              </li>
              <li class="flex items-start">
                <div class="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center mr-3 mt-1">
                  <i class="fas fa-check text-orange-600 text-sm"></i>
                </div>
                <p class="text-gray-700">Personalize customer experiences at scale</p>
              </li>
            </ul>
          </div>
          <div class="md:w-1/2">
            <div class="bg-gray-100 rounded-xl p-6 shadow-lg">
              <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1000" 
                   alt="Behavioral Analysis Dashboard showing customer journey mapping" 
                   class="rounded-lg w-full">
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Testimonials Section -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-4 text-gray-900">What Our Clients Say</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Hear from business leaders who have transformed their customer strategies with our platform.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          @for (testimonial of testimonials; track testimonial.author) {
            <div class="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
              <div class="text-orange-600 text-4xl mb-4">
                <i class="fas fa-quote-left"></i>
              </div>
              <p class="text-gray-700 mb-6 italic">{{ testimonial.quote }}</p>
              <div class="flex items-center">
                <div class="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold mr-4">
                  {{ testimonial.author.charAt(0) }}
                </div>
                <div>
                  <p class="font-bold text-gray-900">{{ testimonial.author }}</p>
                  <p class="text-gray-600 text-sm">{{ testimonial.position }}, {{ testimonial.company }}</p>
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="py-16 bg-gray-50" id="faq">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Frequently Asked Questions</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Get answers to common questions about our Customer Insight platform.
          </p>
        </div>

        <div class="max-w-4xl mx-auto">
          @for (faq of faqs; track faq.question) {
            <div class="mb-6 border-b border-gray-200 pb-6">
              <h3 class="text-xl font-bold mb-3 text-gray-900">{{ faq.question }}</h3>
              <p class="text-gray-600">{{ faq.answer }}</p>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-16 bg-gradient-to-br from-orange-900 to-blue-900 text-white" id="contact">
      <div class="container mx-auto px-4">
        <div class="max-w-4xl mx-auto text-center">
          <h2 class="text-3xl md:text-4xl font-bold mb-6">Ready to Transform Your Customer Strategy?</h2>
          <p class="text-xl mb-8">
            Join industry leaders who rely on our Customer Insight platform to drive growth and customer loyalty.
          </p>
          <div class="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-lg">
            <h3 class="text-2xl font-bold mb-6">Request a Demo</h3>
            <form class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium mb-2">First Name</label>
                <input type="text" class="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-orange-400">
              </div>
              <div>
                <label class="block text-sm font-medium mb-2">Last Name</label>
                <input type="text" class="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-orange-400">
              </div>
              <div>
                <label class="block text-sm font-medium mb-2">Company Email</label>
                <input type="email" class="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-orange-400">
              </div>
              <div>
                <label class="block text-sm font-medium mb-2">Company</label>
                <input type="text" class="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-orange-400">
              </div>
              <div class="md:col-span-2">
                <label class="block text-sm font-medium mb-2">How can we help you?</label>
                <textarea rows="4" class="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-orange-400"></textarea>
              </div>
              <div class="md:col-span-2">
                <button type="submit" class="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 px-6 rounded-lg transition duration-300">
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [`
    .bg-pattern {
      background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    }
  `]
})
export class CustomerInsightComponent {
  features: Feature[] = [
    {
      icon: 'fa-users',
      title: 'Advanced Segmentation',
      description: 'Sophisticated customer segmentation using AI algorithms that analyze behavioral patterns, purchase history, demographics, and engagement metrics to identify high-value customer groups.'
    },
    {
      icon: 'fa-route',
      title: 'Customer Journey Mapping',
      description: 'Comprehensive visualization and analysis of customer journeys across all touchpoints, identifying friction points, conversion opportunities, and moments that matter.'
    },
    {
      icon: 'fa-chart-bar',
      title: 'Behavioral Analytics',
      description: 'Deep analysis of customer behavior patterns, preferences, and decision-making processes using machine learning to predict future actions and personalize experiences.'
    },
    {
      icon: 'fa-bullhorn',
      title: 'Voice of Customer',
      description: 'Integrated collection and analysis of customer feedback across multiple channels, using natural language processing to identify sentiment, themes, and actionable insights.'
    },
    {
      icon: 'fa-brain',
      title: 'Predictive Modeling',
      description: 'AI-powered predictive models that forecast customer lifetime value, churn risk, purchase propensity, and next best actions to optimize customer relationships.'
    },
    {
      icon: 'fa-chart-pie',
      title: 'Performance Dashboard',
      description: 'Customizable real-time dashboards displaying key customer metrics, trends, and insights with drill-down capabilities for detailed analysis and decision support.'
    }
  ];

  insightCategories: InsightCategory[] = [
    {
      name: 'Behavioral Insights',
      description: 'Understand how customers interact with your products and services across all touchpoints and channels.',
      icon: 'fa-chart-bar'
    },
    {
      name: 'Preference Analysis',
      description: 'Identify customer preferences and interests to tailor your offerings and marketing messages.',
      icon: 'fa-heart'
    },
    {
      name: 'Lifecycle Mapping',
      description: 'Track and optimize the entire customer lifecycle from acquisition to retention and loyalty.',
      icon: 'fa-circle-notch'
    },
    {
      name: 'Voice of Customer',
      description: 'Capture and analyze customer feedback from multiple sources to understand needs and pain points.',
      icon: 'fa-microphone'
    }
  ];

  testimonials: Testimonial[] = [
    {
      quote: "Customer Insight has transformed how we understand and engage with our customers. We've seen a 35% increase in customer retention since implementing the platform.",
      author: "Jennifer Williams",
      position: "Chief Marketing Officer",
      company: "Retail Innovations Inc."
    },
    {
      quote: "The predictive analytics capabilities have been game-changing for our business. We can now anticipate customer needs before they even express them.",
      author: "David Chen",
      position: "VP of Customer Experience",
      company: "Global Services Group"
    },
    {
      quote: "The segmentation tools have allowed us to create highly targeted campaigns that resonate with our customers. Our conversion rates have increased by 42%.",
      author: "Sarah Johnson",
      position: "Director of Digital Marketing",
      company: "Consumer Brands International"
    }
  ];

  faqs: FAQ[] = [
    {
      question: "How does Customer Insight gather and analyze customer data?",
      answer: "Our platform integrates with your existing data sources, including CRM systems, website analytics, social media, and customer service platforms. We use AI and machine learning to analyze this data and extract meaningful insights about customer behavior, preferences, and needs."
    },
    {
      question: "Can Customer Insight help with personalization at scale?",
      answer: "Yes, our platform includes powerful personalization tools that allow you to deliver tailored experiences to each customer segment. Our AI-driven recommendation engine can automatically personalize content, product recommendations, and marketing messages based on individual customer profiles."
    },
    {
      question: "How does the platform help improve customer retention?",
      answer: "Customer Insight identifies patterns that indicate potential churn risk and provides early warning alerts. It also helps you understand the factors driving customer loyalty, allowing you to implement targeted retention strategies and measure their effectiveness."
    },
    {
      question: "Can the platform integrate with our existing marketing tools?",
      answer: "Yes, Customer Insight offers seamless integration with most major marketing platforms, CRM systems, and analytics tools. Our API allows for easy data exchange with your existing technology stack."
    },
    {
      question: "How quickly can we expect to see results after implementing Customer Insight?",
      answer: "Most clients begin to see valuable insights within the first few weeks of implementation. Measurable business results typically follow within 2-3 months as you implement the strategies informed by these insights."
    }
  ];
} 